import React, { useRef, useState } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { useSuspenselessRefetch } from '@jira/issue__use-suspenseless-refetch/src/index.tsx';
import { token } from '@atlaskit/tokens';
import type { issues_softwareReleasesVersionDetailIssueList_jiraIssueEdgesReference$key } from '@atlassian/jira-relay/src/__generated__/issues_softwareReleasesVersionDetailIssueList_jiraIssueEdgesReference.graphql';
import type { issues_softwareReleasesVersionDetailIssueList_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/issues_softwareReleasesVersionDetailIssueList_jiraVersion.graphql';
import type { issues_softwareReleasesVersionDetailIssueList_jiraVersionRefetch$key } from '@atlassian/jira-relay/src/__generated__/issues_softwareReleasesVersionDetailIssueList_jiraVersionRefetch.graphql';
import DEVOPS_PROVIDERS_REFETCH_QUERY, {
	type versionDetailIssueListJiraVersionRefetchQuery,
} from '@atlassian/jira-relay/src/__generated__/versionDetailIssueListJiraVersionRefetchQuery.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { FeatureFlagEmptyStateExperienceModal } from './feature-flags-empty-state/index.tsx';
import { IssueCard } from './issue-card/index.tsx';

interface Props {
	jiraIssueEdgesReference: issues_softwareReleasesVersionDetailIssueList_jiraIssueEdgesReference$key;
	jiraVersion: issues_softwareReleasesVersionDetailIssueList_jiraVersion$key;
	jiraVersionRefetch: issues_softwareReleasesVersionDetailIssueList_jiraVersionRefetch$key;
}

export const Issues = ({ jiraIssueEdgesReference, jiraVersion, jiraVersionRefetch }: Props) => {
	const [isFeatureFlagEmptyStateModalOpen, setIsFeatureFlagEmptyStateModalOpen] = useState(false);
	const featureFlagButtonRef = useRef(null);
	const jiraIssueEdgeReferenceData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issues_softwareReleasesVersionDetailIssueList_jiraIssueEdgesReference on JiraIssueEdge
			@relay(plural: true) {
				node @required(action: THROW) {
					key @required(action: THROW)
					devOpsSummarisedEntities @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						summarisedDeployments {
							count
						}
						summarisedBuilds {
							count
						}
					}
					summarisedDeployments @include(if: $shouldIncludeDevOpsFieldsViaAgs) {
						count
					}
					summarisedBuilds @include(if: $shouldIncludeDevOpsFieldsViaAgs) {
						count
					}
					devSummaryCache @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						devSummary {
							pullrequest {
								overall {
									count
								}
							}
							branch {
								overall {
									count
								}
							}
							review {
								overall {
									count
								}
							}
							commit {
								overall {
									count
								}
							}
							build {
								overall {
									count
								}
							}
							deploymentEnvironments {
								overall {
									topEnvironments {
										status
									}
								}
							}
						}
					}
					...issueCard_softwareReleasesVersionDetailIssueList_jiraIssue
				}
			}
		`,
		jiraIssueEdgesReference,
	);

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issues_softwareReleasesVersionDetailIssueList_jiraVersion on JiraVersion {
				...issueCard_softwareReleasesVersionDetailIssueList_jiraVersion
			}
		`,
		jiraVersion,
	);

	const [versionRefetch, refetch] = useRefetchableFragment<
		versionDetailIssueListJiraVersionRefetchQuery,
		issues_softwareReleasesVersionDetailIssueList_jiraVersionRefetch$key
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment issues_softwareReleasesVersionDetailIssueList_jiraVersionRefetch on JiraVersion
			@refetchable(queryName: "versionDetailIssueListJiraVersionRefetchQuery") {
				id
				devOpsSummarisedEntities @skip(if: $isDevopsFeatureDisabledInFedRamp) {
					providers @required(action: NONE) {
						featureFlagProviders @required(action: NONE) {
							__typename
							...featureFlagsEmptyState_softwareReleasesVersionDetailIssueList_FeatureFlagEmptyStateExperienceModal
						}
					}
				}
			}
		`,
		jiraVersionRefetch,
	);

	const [refetchDevOpsProviders] = useSuspenselessRefetch(DEVOPS_PROVIDERS_REFETCH_QUERY, refetch, {
		fetchPolicy: 'network-only',
	});

	const featureFlagProviders = (
		versionRefetch.devOpsSummarisedEntities?.providers.featureFlagProviders ?? []
	).filter(Boolean);

	const nodes = jiraIssueEdgeReferenceData?.map((edge) => edge.node).filter(Boolean);

	// TODO: This will be improved by getting these values from the server either from Data Depot
	// or ARI Graph Store relationships. The current code has a bug that might result incorrect
	// boolean due to pagination. Also this is ugly to calculate in FE.
	const hasBambooDeployment = nodes.some(
		(node) =>
			(
				node.devSummaryCache?.devSummary?.deploymentEnvironments?.overall?.topEnvironments ?? []
			).filter((deployment) => deployment.status === 'DEPLOYED').length > 0,
	);
	const hasDeployments = nodes.some(
		(node) => node.summarisedDeployments?.count != null && node.summarisedDeployments?.count > 0,
	);
	const hasCommits = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.commit?.overall?.count) > 0,
	);
	const hasPullRequests = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.pullrequest?.overall?.count) > 0,
	);
	const hasBranches = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.branch?.overall?.count) > 0,
	);
	const hasBuilds =
		nodes.some((node) => Number(node.summarisedBuilds?.count) > 0) ||
		nodes.some((node) => Number(node.devSummaryCache?.devSummary?.build?.overall?.count) > 0);
	const hasReviews = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.review?.overall?.count) > 0,
	);
	const hasDevelopments = hasCommits || hasPullRequests || hasBranches || hasReviews;

	return (
		<IssuesContainer>
			{nodes.map((node, i) => (
				<IssueCard
					rowNum={i + 1}
					key={node.key}
					jiraIssue={node}
					jiraVersion={version}
					onFeatureFlagEmptyStateModalOpen={setIsFeatureFlagEmptyStateModalOpen}
					featureFlagButtonRef={featureFlagButtonRef}
					hasBambooDeployment={hasBambooDeployment}
					hasDeployments={hasDeployments}
					hasDevelopments={hasDevelopments}
					hasBuilds={hasBuilds}
				/>
			))}
			{!isDevopsFeatureDisabledInFedRamp() && (
				<FeatureFlagEmptyStateExperienceModal
					returnFocusRef={featureFlagButtonRef}
					devOpsFeatureFlagProviders={featureFlagProviders}
					isOpen={isFeatureFlagEmptyStateModalOpen}
					onClose={() => setIsFeatureFlagEmptyStateModalOpen(false)}
					onInstallSuccess={() =>
						refetchDevOpsProviders({
							id: versionRefetch.id,
							isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
						})
					}
				/>
			)}
		</IssuesContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssuesContainer = styled.div({
	zIndex: 1,
	position: 'relative',

	backgroundColor: token('elevation.surface'),
});
